// src/components/layout/Layout.tsx

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';

// ✅ default импорты
import Navigation from './Navigation';
import Header from './Header';

interface LayoutProps {
  children: React.ReactNode;
}

const LayoutContainer = styled.div<{ $isLoginPage: boolean }>`
  /* ✅ КРИТИЧНО для iOS: используем CSS-переменные от Telegram WebApp */
  min-height: var(--tg-viewport-height, 100vh);
  width: 100%;
  max-width: 430px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow-x: hidden;

  /* ✅ Учитываем safe-area для iOS (notch/home indicator) */
  padding-bottom: env(safe-area-inset-bottom, 0px);

  /* ✅ Современный фон с градиентами */
  background: ${props => props.$isLoginPage
    ? `linear-gradient(135deg, #667eea 0%, #764ba2 100%)`
    : `var(--tg-theme-bg-color, #ffffff)`
  };

  ${props => !props.$isLoginPage && `
    background-image:
      radial-gradient(circle at 20% 20%, rgba(123, 104, 238, 0.03) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(135, 206, 235, 0.03) 0%, transparent 50%);
  `}

  /* ✅ На больших экранах показываем как мобильное приложение */
  @media (min-width: 769px) {
    box-shadow: 0 0 50px rgba(0, 0, 0, 0.1);
    border-radius: 24px;
    margin-top: 20px;
    min-height: calc(var(--tg-viewport-height, 100vh) - 40px);
    overflow: hidden;
  }
`;

const HeaderWrapper = styled.div<{ $isLoginPage: boolean }>`
  /* ✅ ФИКСИРОВАННЫЙ HEADER */
  position: sticky;
  top: 0;
  z-index: 100;
  display: ${props => props.$isLoginPage ? 'none' : 'block'};

  /* ✅ Современный glassmorphism */
  background: var(--surface-glass);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid var(--border-light);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);

  /* ✅ Учитываем safe area */
  padding-top: env(safe-area-inset-top, 0px);

  [data-theme="dark"] & {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  }
`;

const MainContent = styled(motion.main)<{ $isLoginPage: boolean }>`
  /* ✅ FLEX LAYOUT ДЛЯ ПРАВИЛЬНОГО РАСПРЕДЕЛЕНИЯ ПРОСТРАНСТВА */
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 0; /* Важно для flex */

  /* ✅ КРИТИЧНО для iOS: используем CSS-переменные от Telegram WebApp */
  /* viewportStableHeight не меняется при появлении клавиатуры */
  min-height: ${props => props.$isLoginPage
    ? 'var(--tg-viewport-stable-height, 100vh)'
    : 'calc(var(--tg-viewport-stable-height, 100vh) - 60px)'
  }; /* 60px header */

  /* ✅ Прокрутка только внутри контента */
  overflow-y: auto;
  overflow-x: hidden;

  /* ✅ Smooth scrolling */
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
`;

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isLoginPage = location.pathname === '/login' || location.pathname === '/welcome';

  return (
    <LayoutContainer $isLoginPage={isLoginPage}>
      {/* ✅ Header - только для авторизованных страниц */}
      <HeaderWrapper $isLoginPage={isLoginPage}>
        <Header 
          currentPath={location.pathname}
          onBack={() => navigate(-1)}
          onProfileClick={() => navigate('/profile')}
        />
      </HeaderWrapper>

      {/* ✅ Основной контент */}
      <MainContent
        $isLoginPage={isLoginPage}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
      >
        {children}
      </MainContent>

      {/* ✅ Bottom Navigation - только для авторизованных страниц */}
      {!isLoginPage && (
        <Navigation 
          currentPath={location.pathname}
          onNavigate={(path) => navigate(path)}
        />
      )}
    </LayoutContainer>
  );
};
